import { useEffect, useState, useMemo } from 'react';
import { useOutletContext } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { OutletContext } from '../../layout/Layout';
import PageTitleSubheader from '../../layout/subheader/PageTitleSubheader';
import {
	Card,
	CardBody,
	Flex,
	FlexItem,
	Form,
	FormGroup,
	Grid,
	GridItem,
	Label,
	SearchInput,
	Text,
	Title,
} from '@patternfly/react-core';
import { Link } from 'react-router-dom';
import ZiCard from '../../components/card/ZiCard';
import { CardTypesEnum } from '../../enums/card-types.enum';
import './Discover.scss';
import { CategoryQuestion, DiscoverCategory, QuickFilter } from '../../types/discover';
import { useMount } from 'react-use';
import { QuestionCategory } from '../../api/question-category/QuestionCategory';
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons';
import EmptyResults from '../../components/EmptyResults';
import { iconMap } from '../../helpers/global-icon-map.helper';
import { Tag, TTag } from '../../api/tags/Tags';
import { TNewDateRange } from '../../api/types/TNewDateRange';
import {
	SubNavContext,
	TipAndTrickPageTypeEnum,
} from '../../helpers/sub-nav-contexts/sub-nav-context';
import { ZoneSetting, ZoneSettingEnum } from '../../api/zone/ZoneSetting';
import { TQuestion } from '../../api/questions/Questions';
import { Switch } from '@patternfly/react-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSparkles } from '@fortawesome/pro-solid-svg-icons';
import Category from './Category';
import Question from './Question';
import DiscoverPageContext from '../../helpers/page-contexts/discover-page-context';
import DatePeriodSelector from '../../components/date-period-selector/DatePeriodSelector';

// Synonym map — keys and values are all lowercase
const SYNONYM_MAP: Record<string, string[]> = {
	compensation: ['compensate', 'compensated', 'pay', 'salary', 'wage', 'remuneration'],
	compensate: ['compensation', 'pay', 'salary', 'wage'],
	pay: ['compensation', 'compensate', 'salary', 'wage'],
	salary: ['pay', 'compensation', 'wage', 'compensate'],
	hire: ['hiring', 'hired', 'recruitment', 'recruit', 'onboard'],
	hiring: ['hire', 'hired', 'recruitment', 'recruit'],
	recruitment: ['hire', 'hiring', 'recruit', 'onboard'],
	turnover: ['attrition', 'churn', 'resignation', 'resign'],
	attrition: ['turnover', 'churn', 'resignation'],
	performance: ['perform', 'review', 'appraisal', 'evaluation'],
	headcount: ['employees', 'staff', 'workforce', 'personnel', 'count'],
	employee: ['employees', 'staff', 'worker', 'personnel', 'headcount'],
	diversity: ['inclusion', 'equity', 'dei', 'belonging'],
	training: ['learning', 'development', 'upskill', 'education'],
	engagement: ['satisfaction', 'morale', 'sentiment', 'wellbeing'],
	absence: ['absenteeism', 'leave', 'sick', 'time off'],
	leave: ['absence', 'absenteeism', 'sick', 'time off'],
};

/**
 * Expands a search term into itself plus any known synonyms
 */
const expandTerms = (term: string): string[] => {
	const lower = term.toLowerCase();
	const synonyms = SYNONYM_MAP[lower] ?? [];
	return [lower, ...synonyms];
};

/**
 * Returns true if the target string matches any of the expanded search terms
 */
const matchesSearch = (target: string, expandedTerms: string[]): boolean => {
	const lower = target.toLowerCase();
	return expandedTerms.some((t) => lower.includes(t));
};

type CategorySearchResult = {
	category: DiscoverCategory;
	matchedQuestions: TQuestion[];
	categoryNameMatches: boolean;
};

const Discover = () => {
	const navigate = useNavigate();
	const { setSubSide, subNavExpanded, setSubNavExpanded } = useOutletContext<OutletContext>();
	const [searchTerm, setSearchTerm] = useState<string>('');
	const [categories, setCategories] = useState<DiscoverCategory[]>();
	const [hasLoadedCategories, setHasLoadedCategories] = useState<boolean>(false);
	const [quickFilters, setQuickFilters] = useState<QuickFilter[]>();
	const [filteredQuickFilters, setFilteredQuickFilters] = useState<QuickFilter[]>();
	const [selectedDate, setSelectedDate] = useState<TNewDateRange>();
	const [subNavContext, setSubNavContext] = useState<SubNavContext>();
	const [allQuestions, setAllQuestions] = useState<TQuestion[]>();
	const [aiSearchEnabled, setAiSearchEnabled] = useState(false);
	const [inlineCategoryId, setInlineCategoryId] = useState<number | undefined>(undefined);

	// Check if we're on the search route (not just doing live search typing)
	const isOnSearchRoute = window.location.pathname === '/discover/search';

	// Open a category inline; if the same category is clicked again, force a replace
	const openCategory = (id: number) => {
		if (inlineCategoryId === id) {
			// unset then set to force re-render/reload
			setInlineCategoryId(undefined);
			setTimeout(() => setInlineCategoryId(id), 0);
		} else {
			setInlineCategoryId(id);
		}
	};

	// update the search handler
	const handleSearch = (_event: any, value: string) => {
		if (aiSearchEnabled) {
			navigate(`/discover/analyst?term=${value}`);
		} else {
			navigate(`/discover/search?term=${value}`);
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' && searchTerm && searchTerm.trim().length > 0) {
			if (aiSearchEnabled) {
				navigate(`/discover/analyst?term=${searchTerm}`);
			} else {
				navigate(`/discover/search?term=${searchTerm}`);
			}
		}
	};

	useMount(() => {
		setSubNavContext({
			...subNavContext,
			TipAndTrickPageType: TipAndTrickPageTypeEnum.DISCOVER,
		});

		const categoryQuestions: TQuestion[] = [];

		void QuestionCategory.GetPermitted(['questions']).then((response) => {
			const discoverCategories: DiscoverCategory[] = [];

			response.map((item) => {
				const newCategory: DiscoverCategory = {
					icon: iconMap[item.icon],
					id: item.id!,
					key: `Category_${item.title}`,
					name: item.title,
					sequence: item.sequence ?? 0,
					questions: [],
				};
				discoverCategories.push(newCategory);

				const questions: TQuestion[] = (
					(item.questions as TQuestion[]) ?? ([] as TQuestion[])
				).map((q: TQuestion) => ({
					chart: q.chart,
					description: q.description,
					name: q.name,
					sequence: q.sequence,
					category: newCategory.id,
				}));

				questions.map((q) => {
					categoryQuestions.push(q);
				});
			});

			setCategories(
				discoverCategories.sort((a, b) => {
					const seqDiff = (a.sequence ?? 0) - (b.sequence ?? 0);
					if (seqDiff !== 0) return seqDiff;
					return a.name.localeCompare(b.name);
				})
			);
			setAllQuestions(categoryQuestions);
			setHasLoadedCategories(true);
		});

		void Tag.GetPermitted().then((response) => {
			const filters: QuickFilter[] = [];
			response.map((tag) => {
				const tagName = tag.id!.toString();
				const quickFilter: QuickFilter = {
					key: `QuickFilter_${tag.name}_${tagName}`,
					name: tag.name,
				};
				filters.push(quickFilter);
			});
			setQuickFilters(filters);
		});
	});

	useEffect(() => {
		if (quickFilters && quickFilters.length > 0) {
			void ZoneSetting.Get(ZoneSettingEnum.discoverQuickFilters).then((response) => {
				if (response.enabled && response.value) {
					const quickFiltersZoneSetting = response.value.toLowerCase().split(',');
					const finalQuickFilters: QuickFilter[] = [];

					quickFiltersZoneSetting.forEach((zoneQuickFilter) => {
						const item = quickFilters.find(
							(x) =>
								x.name.toLowerCase().replace(/\s+/g, '') ===
								zoneQuickFilter.replace(/-/g, '')
						);
						if (item) {
							finalQuickFilters.push({
								key: `QuickFilter_${item.name}_${item.name}`,
								name: item.name,
							});
						}
					});

					if (finalQuickFilters.length < 10) {
						const countToAdd = 10 - finalQuickFilters.length;
						let added = 0;

						for (let i = 0; i < quickFilters.length && added < countToAdd; i++) {
							const item = quickFilters[i];
							const alreadyExists = finalQuickFilters.some(
								(qf) => qf.name === item.name
							);
							if (!alreadyExists) {
								finalQuickFilters.push({
									key: `QuickFilter_${item.name}_${item.name}`,
									name: item.name,
								});
								added++;
							}
						}
					}

					setFilteredQuickFilters(finalQuickFilters);
				} else {
					setFilteredQuickFilters(quickFilters);
				}
			});
		}
	}, [quickFilters]);

	useEffect(() => {
		setSubSide({
			subheaderContext: (
				<PageTitleSubheader
					pageTitle="Discover"
					pageDescription="Welcome to where you can find something specific or have a topic or area of interest. Looking for a specific Report, Chart, or Dashboard? You can find it here. Not sure what you are looking for or what you want to do, we got you."
					expanded={subNavExpanded}
					setExpanded={setSubNavExpanded}
					isCollapsable
					subNavContext={subNavContext}
				/>
			),
			sidebarContext: null,
			hideLeftSideBar: true,
		});
	}, [setSubSide, subNavExpanded, setSubNavExpanded, subNavContext]);

	// Live search — fires when searchTerm >= 3 chars
	const isSearchActive = searchTerm.trim().length >= 3;

	const searchResults = useMemo<CategorySearchResult[]>(() => {
		if (!isSearchActive || !categories || !allQuestions) return [];

		// Expand each word in the search term into synonyms
		const words = searchTerm.trim().toLowerCase().split(/\s+/);
		const expandedTerms = Array.from(new Set(words.flatMap(expandTerms)));

		return categories
			.map((category) => {
				const categoryNameMatches = matchesSearch(category.name, expandedTerms);

				// Find questions belonging to this category that match
				const matchedQuestions = allQuestions.filter(
					(q) =>
						q.category === category.id &&
						(matchesSearch(q.name ?? '', expandedTerms) ||
							matchesSearch(q.description ?? '', expandedTerms))
				);

				return { category, matchedQuestions, categoryNameMatches };
			})
			.filter((r) => r.categoryNameMatches || r.matchedQuestions.length > 0);
	}, [searchTerm, categories, allQuestions, isSearchActive]);

	const matchingCategoryCount = searchResults.length;
	const totalQuestionCount = searchResults.reduce((sum, r) => sum + r.matchedQuestions.length, 0);

	// Derive which categories to show in the card grid
	const visibleCategories = useMemo(() => {
		if (!isSearchActive || searchResults.length === 0) return categories;
		const matchedIds = new Set(searchResults.map((r) => r.category.id));
		return categories?.filter((c) => matchedIds.has(c.id));
	}, [isSearchActive, searchResults, categories]);

	return (
		<Card className="discover-container">
			<CardBody>
				<Form>
					<Grid
						hasGutter
						md={6}
					>
						<GridItem span={8}>
							<FormGroup>
								<Flex
									alignItems={{ default: 'alignItemsCenter' }}
									spaceItems={{ default: 'spaceItemsSm' }}
								>
									<FlexItem grow={{ default: 'grow' }}>
										<div style={{ position: 'relative' }}>
											{/* Left sparkle — shows inside input on the left of the search icon */}
											{aiSearchEnabled && (
												<FontAwesomeIcon
													icon={faSparkles}
													style={{
														position: 'absolute',
														left: '10px',
														top: '28%',
														transform: 'translateY(-50%)',
														color: '#06c',
														zIndex: 1,
														pointerEvents: 'none',
														fontSize: '13px',
													}}
												/>
											)}
											<SearchInput
												placeholder={
													aiSearchEnabled
														? 'Ask AI anything about your data...'
														: isSearchActive
														? 'Searching categories and questions...'
														: 'Type at least 3 characters to search...'
												}
												value={searchTerm}
												onChange={(_event, value: string) =>
													setSearchTerm(value)
												}
												className={`${
													aiSearchEnabled ? 'hide-search-icon' : ''
												}`}
												onClear={() => setSearchTerm('')}
												onSearch={handleSearch}
												onKeyDown={handleKeyDown}
												aria-label="Discover Search"
												data-testid="discover-search"
												style={
													aiSearchEnabled
														? {
																paddingLeft: '30px',

																borderRadius: '5px',
														  }
														: undefined
												}
											/>
											<div style={{ marginTop: '6px' }}>
												<Text
													component="small"
													style={{
														color: aiSearchEnabled ? '#06c' : '#666',
														fontWeight: aiSearchEnabled ? 600 : 400,
														whiteSpace: 'nowrap',
														transition: 'color 0.2s',
													}}
												>
													{aiSearchEnabled ? (
														<>
															<FontAwesomeIcon
																icon={faSparkles}
																style={{
																	marginRight: '4px',
																	color: '#06c',
																}}
															/>
															AI Search Enabled
														</>
													) : (
														'AI Search Off'
													)}
												</Text>
											</div>

											{/* Right sparkle — shows left of the search arrow */}
											{aiSearchEnabled && (
												<FontAwesomeIcon
													icon={faSparkles}
													style={{
														position: 'absolute',
														right: '38px',
														top: '28%',
														transform: 'translateY(-50%)',
														color: '#06c',
														zIndex: 1,
														pointerEvents: 'none',
														fontSize: '13px',
													}}
												/>
											)}
										</div>
									</FlexItem>
									<FlexItem>
										<Flex
											alignItems={{ default: 'alignItemsCenter' }}
											spaceItems={{ default: 'spaceItemsXs' }}
										>
											<FlexItem></FlexItem>
											<FlexItem style={{ paddingBottom: '28px' }}>
												<Switch
													id="ai-search-toggle"
													aria-label="Toggle AI Search"
													isChecked={aiSearchEnabled}
													onChange={(checked) =>
														setAiSearchEnabled(checked)
													}
												/>
											</FlexItem>
										</Flex>
									</FlexItem>
								</Flex>
							</FormGroup>
						</GridItem>

						<GridItem span={12}>
							<FormGroup>
								<Flex>
									<Text>Quick Filters:</Text>
									{filteredQuickFilters &&
										filteredQuickFilters.slice(0, 10).map((filter, index) => (
											<Label
												key={index}
												className="quickfilter-label"
												style={{ backgroundColor: '#f5f5f5' }}
												render={({ className, content }) => (
													<Link
														to={`/discover/search?tag=${filter.name}`}
														className={className}
													>
														{content}
													</Link>
												)}
												data-testid={`discover-quickfilter-${filter.key}`}
											>
												{filter.name}
											</Label>
										))}
								</Flex>
							</FormGroup>
						</GridItem>

						{/* Live search results panel */}
						{isSearchActive && (
							<GridItem span={12}>
								<div className="discover-search-results">
									{searchResults.length === 0 ? (
										<EmptyResults
											emptyStateBody={`No categories or questions matched "${searchTerm}"`}
											iconName={faInfoCircle}
											backComponent={<></>}
										/>
									) : (
										<>
											<Flex>
												<div className="filter-container">
													<div className="date-selector-container">
														<DatePeriodSelector
															applyDateRange={setSelectedDate}
														/>
													</div>
												</div>
											</Flex>
											<br />
											<Text
												component="p"
												className="discover-search-summary"
											>
												<strong>{matchingCategoryCount}</strong>{' '}
												{matchingCategoryCount === 1
													? 'category'
													: 'categories'}{' '}
												found &mdash; <strong>{totalQuestionCount}</strong>{' '}
												{totalQuestionCount === 1
													? 'question'
													: 'questions'}{' '}
												matched across all categories
											</Text>

											<div className="discover-search-categories">
												{searchResults.map((result) => {
													if (
														!result.matchedQuestions ||
														result.matchedQuestions.length === 0
													)
														return null;
													return (
														<div
															key={result.category.id}
															className="discover-search-category-block"
														>
															<Flex
																alignItems={{
																	default: 'alignItemsCenter',
																}}
																className="category-title"
															>
																<FlexItem>
																	<FontAwesomeIcon
																		icon={result.category.icon}
																		size="2x"
																	/>
																</FlexItem>
																<FlexItem>
																	<Title headingLevel="h3">
																		{result.category.name}
																	</Title>
																</FlexItem>
																<FlexItem>
																	&mdash;{' '}
																	<span className="discover-search-question-count">
																		{
																			result.matchedQuestions
																				.length
																		}{' '}
																		{result.matchedQuestions
																			.length === 1
																			? 'question'
																			: 'questions'}{' '}
																		found
																	</span>
																</FlexItem>
															</Flex>
															<hr />
															<div className="discover-search-category-questions">
																{result.matchedQuestions.map(
																	(q) => {
																		const cq = {
																			key: `Question_${q.name}`,
																			question: q.name,
																			description:
																				q.description,
																			priority: q.sequence,
																			tags: q.tags
																				? q.tags.map(
																						(t: TTag) =>
																							t.name
																								? t.name
																								: t
																				  )
																				: [],
																			hasChart: q.chart
																				? true
																				: false,
																			chartId: (
																				q.chart ?? 0
																			).toString(),
																			dashboardId: (
																				q.dashboard ?? 0
																			).toString(),
																		} as CategoryQuestion;
																		return (
																			<div className="categories">
																				<Question
																					key={cq.key}
																					question={cq}
																					selectedDate={
																						selectedDate
																					}
																				/>
																			</div>
																		);
																	}
																)}
															</div>
															<br />
														</div>
													);
												})}
											</div>
										</>
									)}
								</div>
							</GridItem>
						)}
						{inlineCategoryId && (
							<GridItem span={12}>
								<Category
									pageContext={DiscoverPageContext}
									inlineCategoryId={inlineCategoryId}
									showBackButton
									hideFilters={isOnSearchRoute}
									hideDateSelector={isOnSearchRoute}
									onBack={() => setInlineCategoryId(undefined)}
								/>
							</GridItem>
						)}

						{!isSearchActive && (
							<>
								{' '}
								<GridItem span={12}>
									<FormGroup>
										<Title headingLevel="h1">
											What do you want to Zero In on?
										</Title>
									</FormGroup>
								</GridItem>
								<GridItem span={12}>
									<FormGroup>
										{visibleCategories?.length === 0 && hasLoadedCategories && (
											<div className="text-center">
												<EmptyResults
													emptyStateBody="No Discover Categories were found"
													iconName={faInfoCircle}
													backComponent={<></>}
												/>
											</div>
										)}
										<Flex className="list-items">
											{visibleCategories &&
												hasLoadedCategories &&
												visibleCategories.map((category, index) => (
													<ZiCard
														key={index}
														url={`/discover/search?id=${category.id}`}
														title={category.name}
														type={CardTypesEnum.Category}
														icon={category.icon}
														hidePin={true}
													/>
												))}
										</Flex>
									</FormGroup>
								</GridItem>
							</>
						)}
					</Grid>
				</Form>
			</CardBody>
		</Card>
	);
};

export default Discover;
